import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { HOME } from "@/constants/testIds";
import {
  ArrowDownRight,
  ArrowUpRight,
  Mail,
  MapPin,
  Calendar,
  BookOpen,
  Building2,
  Megaphone,
  Lock,
  LogIn,
  User as UserIcon,
  Instagram,
  Briefcase,
  Home as HomeIcon,
  TrendingUp,
  Coins,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth, API_BASE, formatApiError } from "@/context/AuthContext";
import Dashboard from "@/pages/Dashboard";
import Login from "@/pages/Login";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Admin from "@/pages/Admin";
import ResetPassword from "@/pages/ResetPassword";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_EMAIL = "devontae@ichventures.net";
const INSTAGRAM_URL = "https://instagram.com/always.dwill";
const TIKTOK_URL = "https://www.tiktok.com/@always.dwill";
const SOCIAL_HANDLE = "@ALWAYS.DWILL";
const FOUNDER_PHOTO = "https://customer-assets.emergentagent.com/job_course-platform-150/artifacts/9wpqob75_Headshot.jpg";
export const ICH_LOGO = "https://customer-assets.emergentagent.com/job_course-platform-150/artifacts/huyogkc1_ICH%20Logo-%20dark.png";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TikTokIcon = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
    <path d="M19.321 5.562a5.124 5.124 0 0 1-3.414-1.267 5.124 5.124 0 0 1-1.537-2.995V1h-3.061v13.017c0 .924-.751 1.674-1.675 1.674a1.675 1.675 0 0 1 0-3.35c.185 0 .363.03.53.086V9.311a4.777 4.777 0 0 0-.53-.029 4.756 4.756 0 1 0 4.756 4.756V7.79a8.169 8.169 0 0 0 4.931 1.658V6.386c-.001 0-.001 0 0-.824Z" fill="currentColor"/>
  </svg>
);

// =====================================================================================
// Nav
// =====================================================================================
const Nav = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md" style={{ background: "rgba(10, 22, 40, 0.55)", borderBottom: "1px solid rgba(232, 226, 212, 0.06)" }}>
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
        <button data-testid={HOME.navHome} onClick={() => scrollTo("hero")} className="flex items-center gap-3 group">
          <div className="w-16 h-16 flex items-center justify-center overflow-hidden" style={{ background: "linear-gradient(135deg, #0a1628 0%, #122446 100%)", border: "1px solid rgba(232,226,212,0.25)" }}>
            <img src={ICH_LOGO} alt="ICH" className="w-full h-full object-contain p-1" />
          </div>
          <div className="hidden sm:block">
            <div className="font-label text-[#efe7d2] underline underline-offset-[6px] decoration-[rgba(232,226,212,0.35)]">ICH Ventures</div>
            <div className="font-label text-[rgba(232,226,212,0.55)] text-[0.62rem] mt-1">Innovative Capital Holdings LLC</div>
          </div>
        </button>
        <div className="hidden md:flex items-center gap-9">
          <button data-testid={HOME.navAbout} onClick={() => scrollTo("about")} className="nav-link">About</button>
          <button data-testid={HOME.navServices} onClick={() => scrollTo("services")} className="nav-link">What We Do</button>
          <button data-testid={HOME.navApproach} onClick={() => scrollTo("consulting")} className="nav-link">Consulting</button>
          <button data-testid={HOME.navStudent} onClick={() => navigate("/login")} className="nav-link">Client Login</button>
        </div>
        {user ? (
          user.role === "admin" ? (
            <Link to="/admin" data-testid={HOME.navDashboard} className="btn-outline">Admin</Link>
          ) : (
            <Link to="/dashboard" data-testid={HOME.navDashboard} className="btn-outline">My Courses</Link>
          )
        ) : (
          <button data-testid={HOME.navGetInTouch} onClick={() => scrollTo("contact")} className="btn-outline">Get in Touch</button>
        )}
      </div>
    </nav>
  );
};

// =====================================================================================
// Hero
// =====================================================================================
const Hero = () => (
  <section id="hero" className="relative pt-40 sm:pt-44 pb-16 px-6 sm:px-10">
    <div className="max-w-[1400px] mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-8 fade-up" style={{ animationDelay: "120ms" }}>
          <h1 className="font-display text-[#efe7d2] text-5xl sm:text-7xl lg:text-[5.5rem] leading-[1.02]">
            Building <span className="font-italic-display text-[rgba(232,226,212,0.85)]">Businesses.</span>
            <br />
            Investing in <span className="font-italic-display text-[rgba(232,226,212,0.85)]">Assets.</span>
            <br />
            Creating <span className="font-italic-display text-[rgba(232,226,212,0.85)]">Long-Term Wealth.</span>
          </h1>
          <div className="flex items-center gap-4 mt-10">
            <div className="hairline w-16" />
            <span className="font-label text-[rgba(232,226,212,0.65)]">ICH Ventures · Est. 2023</span>
            <div className="hairline flex-1 max-w-[180px]" />
          </div>
          <p className="mt-8 max-w-xl text-[rgba(232,226,212,0.72)] text-base sm:text-lg leading-relaxed">
            ICH Ventures is a private investment and business development company focused on entrepreneurship, real estate, financial markets, and strategic investments. We build, acquire, and invest in businesses while sharing practical insights from the journey.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <button data-testid={HOME.heroExploreCourses} onClick={() => scrollTo("services")} className="btn-solid">
              What We Do <ArrowDownRight size={14} />
            </button>
            <button data-testid={HOME.heroStartLearning} onClick={() => scrollTo("about")} className="btn-gold">
              About the Founder <ArrowUpRight size={14} />
            </button>
            <button data-testid={HOME.heroConsultation} onClick={() => scrollTo("contact")} className="btn-outline">
              Request a Consultation
            </button>
          </div>
        </div>
        <div className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-[rgba(232,226,212,0.12)] fade-up" style={{ animationDelay: "260ms" }}>
          <div className="font-label text-[rgba(232,226,212,0.55)] mb-6">Core Pillars</div>
          <ul className="space-y-5">
            {[
              { n: "01", t: "Entrepreneurship" },
              { n: "02", t: "Real Estate" },
              { n: "03", t: "Financial Markets" },
              { n: "04", t: "Investments" },
            ].map((it) => (
              <li key={it.n} className="flex items-baseline gap-4">
                <span className="font-label text-[rgba(232,226,212,0.45)] text-[0.62rem]">{it.n}</span>
                <span className="font-display text-[#efe7d2] text-2xl">{it.t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-16 flex items-center gap-6">
        <div className="hairline flex-1" />
        <span className="font-italic-display text-[rgba(232,226,212,0.55)] text-sm">Scroll</span>
        <div className="hairline flex-1" />
      </div>
    </div>
  </section>
);

// =====================================================================================
// About + Founder
// =====================================================================================
const About = () => (
  <section id="about" className="relative py-20 px-6 sm:px-10">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex items-center gap-4 mb-12">
        <div className="hairline w-10" />
        <span className="font-label text-[rgba(232,226,212,0.65)]">About the Firm</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left: logo + heading */}
        <div className="lg:col-span-5 flex flex-col items-start gap-10">
          <div className="relative w-full max-w-[320px] aspect-square mx-auto lg:mx-0">
            <div className="absolute inset-0" style={{ background: "radial-gradient(circle at center, rgba(201,169,110,0.14), transparent 70%)" }} />
            <img src={ICH_LOGO} alt="Innovative Capital Holdings LLC" className="relative w-full h-full object-contain" />
          </div>
          <h2 className="font-display text-[#efe7d2] text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Capital, <br /><span className="font-italic-display">considered.</span>
          </h2>
        </div>

        {/* Right: paragraph */}
        <div className="lg:col-span-7 space-y-6 text-[rgba(232,226,212,0.72)] text-base leading-relaxed">
          <p>ICH Ventures is a private investment and business development company focused on entrepreneurship, real estate, financial markets, and strategic investments — founded in <span className="text-[#efe7d2]">2023</span> on the belief that thoughtful capital, deployed patiently, compounds into meaningful long-term wealth.</p>
          <p>Rather than a single business, ICH is an ecosystem: we build and acquire operating companies, hold and manage real-estate assets, participate in the public markets, and pursue opportunistic investments across other asset classes. Along the way, we share the frameworks, decisions, and lessons that shape each move — for founders and investors interested in the same journey.</p>
        </div>
      </div>

      {/* Featured firm logo removed — placed above About header instead */}

      <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(232,226,212,0.12)] border border-[rgba(232,226,212,0.12)]">
        {[
          { k: "2023", v: "Year Established" },
          { k: "4", v: "Core focus areas" },
          { k: "1", v: "Long-term horizon" },
        ].map((s) => (
          <div key={s.k} className="bg-[#0a1628] p-10">
            <div className="font-display text-[#efe7d2] text-5xl sm:text-6xl">{s.k}</div>
            <div className="font-label text-[rgba(232,226,212,0.55)] mt-4">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-28">
        <div className="flex items-center gap-4 mb-10">
          <div className="hairline w-10" />
          <span className="font-label text-[rgba(232,226,212,0.65)]">About the Founder</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-12 flex justify-center">
            <div data-testid={HOME.founderPhoto} className="aspect-[4/5] glass relative overflow-hidden w-full max-w-[360px]">
              <img src={FOUNDER_PHOTO} alt="Devontae Williams, CEO" className="absolute inset-0 w-full h-full object-cover object-top" />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0a1628] via-[#0a1628]/40 to-transparent" />
              <div className="absolute top-4 left-4 right-4 flex justify-between font-label text-[0.6rem] text-[rgba(232,226,212,0.7)]">
                <span>01</span>
                <span>Portrait</span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex justify-between font-label text-[0.6rem] text-[rgba(232,226,212,0.7)]">
                <span>ICH</span>
                <span>— Founder</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-12 max-w-3xl mx-auto text-center" data-testid={HOME.founderBio}>
            <h3 className="font-display text-[#efe7d2] text-3xl sm:text-4xl leading-[1.1]">
              Devontae Williams
              <span className="block font-italic-display text-[rgba(232,226,212,0.7)] text-2xl sm:text-3xl mt-2">Chief Executive Officer</span>
            </h3>
            <div className="mt-8 space-y-5 text-[rgba(232,226,212,0.78)] text-base leading-[1.85] text-left">
              <p><span className="text-[#efe7d2] font-medium">Devontae Williams</span> is the Chief Executive Officer of Innovative Capital Holdings LLC (ICH), a consulting and management firm specializing in strategic business solutions across the real estate and digital marketing industries.</p>
              <p>With a background in corporate finance, FP&amp;A, and operational strategy, Devontae has built a career helping organizations improve financial performance, optimize operations, and make data-driven decisions. Drawing on experience supporting multi-million-dollar manufacturing operations, he brings a disciplined, analytical approach to business consulting while leveraging entrepreneurial insight to help clients identify opportunities for growth, operational efficiency, and long-term value creation.</p>
              <p>Through ICH, his mission is to deliver practical, results-oriented solutions that empower businesses and investors to achieve sustainable success.</p>
            </div>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              {["Corporate Finance", "FP&A", "Operational Strategy", "Real Estate", "Digital Marketing"].map((t) => (
                <span key={t} className="font-label text-[rgba(232,226,212,0.7)] border border-[rgba(232,226,212,0.18)] px-3 py-2">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// =====================================================================================
// Pillars — What We Do (replaces course-selling section)
// =====================================================================================
const Services = () => {
  const pillars = [
    {
      id: "entrepreneurship", n: "01", title: "Entrepreneurship", icon: Briefcase,
      desc: "Building and acquiring businesses. We start, buy, and operate companies where disciplined execution can compound into durable equity value.",
      tags: ["Founding", "Acquisitions", "Operations", "Value Creation"],
      testid: HOME.serviceCard1,
    },
    {
      id: "real-estate", n: "02", title: "Real Estate", icon: HomeIcon,
      desc: "Residential and commercial investing. From single-family rentals to opportunistic commercial deals, we underwrite for cash flow, appreciation, and long-hold resilience.",
      tags: ["Residential", "Commercial", "Cash Flow", "Long Hold"],
      testid: HOME.serviceCard2,
    },
    {
      id: "financial-markets", n: "03", title: "Financial Markets", icon: TrendingUp,
      desc: "Index trading and market education. Systematic exposure to public markets with a bias toward index-based strategies, disciplined risk, and continuous learning.",
      tags: ["Index Strategies", "Risk Discipline", "Education"],
      testid: HOME.serviceCard3,
    },
    {
      id: "investments", n: "04", title: "Investments", icon: Coins,
      desc: "Exploring opportunities across multiple asset classes. Selective private and alternative investments — evaluated for asymmetric risk-reward and long-term alignment.",
      tags: ["Private", "Alternatives", "Asymmetric Bets"],
      testid: "service-card-investments",
    },
  ];

  return (
    <section id="services" className="relative py-32 px-6 sm:px-10" style={{ borderTop: "1px solid rgba(232,226,212,0.08)" }}>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <div className="hairline w-10" />
          <span className="font-label text-[rgba(232,226,212,0.65)]">What We Do</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
          <h2 className="lg:col-span-7 font-display text-[#efe7d2] text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Four pillars. <br /><span className="font-italic-display">One long-term thesis.</span>
          </h2>
          <p className="lg:col-span-5 text-[rgba(232,226,212,0.7)] leading-relaxed">
            ICH Ventures operates across a small, deliberate set of disciplines — each chosen for the compounding potential it offers when paired with patient capital and disciplined judgment. Below is where we spend our time.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(232,226,212,0.1)] border border-[rgba(232,226,212,0.1)]">
          {pillars.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.n} data-testid={it.testid} className="bg-[#0a1628] p-10 group transition-colors duration-500 hover:bg-[#0c1c33] flex flex-col">
                <div className="flex items-start justify-between mb-10">
                  <span className="font-label text-[rgba(232,226,212,0.45)]">{it.n}</span>
                  <Icon size={26} strokeWidth={1.25} className="text-[rgba(232,226,212,0.55)] group-hover:text-[#d9b87d] transition-colors" />
                </div>
                <h3 className="font-display text-[#efe7d2] text-2xl sm:text-3xl leading-[1.15]">{it.title}</h3>
                <p className="mt-6 text-[rgba(232,226,212,0.7)] text-sm leading-[1.75] flex-1">{it.desc}</p>
                <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
                  {it.tags.map((t) => (<span key={t} className="font-label text-[rgba(232,226,212,0.55)] text-[0.62rem]">· {t}</span>))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 glass p-10 sm:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="font-label text-[rgba(232,226,212,0.55)] mb-4">Get in touch</div>
              <h3 className="font-display text-[#efe7d2] text-3xl sm:text-4xl leading-[1.15]">
                Want to <span className="font-italic-display">connect</span>, partner, or learn more?
              </h3>
              <p className="mt-4 text-[rgba(232,226,212,0.7)] leading-relaxed max-w-xl">
                Whether you&rsquo;re a founder, investor, or fellow operator — we&rsquo;d be glad to hear what you&rsquo;re working on.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <button data-testid={HOME.ctaStartLearning} onClick={() => scrollTo("contact")} className="btn-solid">Get in Touch <ArrowUpRight size={14} /></button>
              <button data-testid={HOME.ctaConsultation} onClick={() => scrollTo("consulting")} className="btn-outline">Our Approach</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// =====================================================================================
// Consulting (renamed from Approach)
// =====================================================================================
const Consulting = () => {
  const [buying, setBuying] = useState(false);
  const phases = [
    { n: "Phase I", t: "Book", d: "Reserve your 30-minute call for $65. You'll receive a scheduling link by email plus instructions to create your client portal." },
    { n: "Phase II", t: "Prep", d: "Create your account and upload your materials — deals, docs, questions, context. Materials must be uploaded at least 24 hours before the call." },
    { n: "Phase III", t: "Call", d: "A focused 30-minute conversation — direct, honest, and tailored to your specific situation." },
    { n: "Phase IV", t: "Deliver", d: "Detailed notes, commentary, analysis, and recommendations posted privately to your client portal." },
  ];
  const handleBook = async () => {
    setBuying(true);
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { consultation_id: "consultation-30", origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start booking.");
      setBuying(false);
    }
  };
  return (
    <section id="consulting" className="relative py-32 px-6 sm:px-10" style={{ borderTop: "1px solid rgba(232,226,212,0.08)" }}>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <div className="hairline w-10" />
          <span className="font-label text-[rgba(232,226,212,0.65)]">Consulting</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
          <h2 className="lg:col-span-7 font-display text-[#efe7d2] text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Book a private <br /><span className="font-italic-display">30-minute call.</span>
          </h2>
          <p className="lg:col-span-5 text-[rgba(232,226,212,0.7)] leading-relaxed">
            Bring one problem — a deal, a strategy question, a growth roadblock. Upload your materials before the call and, afterwards, receive detailed notes, commentary, analysis, and recommendations posted privately to your client portal.
          </p>
        </div>

        <div className="glass p-8 sm:p-10 mb-12 flex flex-wrap items-center justify-between gap-6" data-testid="book-consultation-card">
          <div>
            <div className="font-label text-[rgba(232,226,212,0.55)]">30-Minute Strategy Consultation</div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-display text-[#efe7d2] text-6xl">$65</span>
              <span className="font-label text-[rgba(232,226,212,0.5)]">one-time · pre-call uploads + written follow-up</span>
            </div>
          </div>
          <button data-testid="book-consultation-btn" disabled={buying} onClick={handleBook} className="btn-solid disabled:opacity-60">
            {buying ? "Loading..." : "Book Consultation"} <ArrowUpRight size={14} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {phases.map((p) => (
            <div key={p.n} className="border-t border-[rgba(232,226,212,0.18)] pt-8">
              <div className="font-label text-[#d9b87d]">{p.n}</div>
              <h4 className="font-display text-[#efe7d2] text-3xl mt-6">{p.t}</h4>
              <p className="mt-5 text-[rgba(232,226,212,0.65)] text-sm leading-[1.75]">{p.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =====================================================================================
// Contact
// =====================================================================================
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please complete every field."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll reply within 1–2 business days.");
      setForm({ name: "", email: "", message: "" });
    } catch (_err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally { setSending(false); }
  };

  return (
    <section id="contact" className="relative py-32 px-6 sm:px-10" style={{ borderTop: "1px solid rgba(232,226,212,0.08)" }}>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <div className="hairline w-10" />
          <span className="font-label text-[rgba(232,226,212,0.65)]">Contact</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <h2 className="font-display text-[#efe7d2] text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Begin the <br /><span className="font-italic-display">conversation.</span>
            </h2>
            <p className="mt-8 text-[rgba(232,226,212,0.72)] leading-relaxed max-w-lg">
              Whether you are enrolling in a course, scaling an operation, evaluating an asset, or positioning a brand for the next chapter — we&rsquo;d be glad to hear what you&rsquo;re working on.
            </p>
            <div className="mt-12 space-y-8">
              <div>
                <div className="font-label text-[rgba(232,226,212,0.5)] mb-3 flex items-center gap-2"><Mail size={12} strokeWidth={1.5} /> Email</div>
                <a data-testid={HOME.contactEmail} href={`mailto:${BUSINESS_EMAIL}`} className="font-display text-[#efe7d2] text-2xl sm:text-3xl hover:text-[#d9b87d] transition-colors">{BUSINESS_EMAIL}</a>
              </div>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="font-label text-[rgba(232,226,212,0.5)] mb-3 flex items-center gap-2"><Calendar size={12} strokeWidth={1.5} /> Availability</div>
                  <div className="font-display text-[#efe7d2] text-xl">By appointment</div>
                </div>
                <div>
                  <div className="font-label text-[rgba(232,226,212,0.5)] mb-3 flex items-center gap-2"><MapPin size={12} strokeWidth={1.5} /> Region</div>
                  <div className="font-display text-[#efe7d2] text-xl">United States</div>
                </div>
              </div>
              <div>
                <div className="font-label text-[rgba(232,226,212,0.5)] mb-3 flex items-center gap-2"><Instagram size={12} strokeWidth={1.5} /> Social</div>
                <div className="flex flex-col gap-2">
                  <a data-testid="contact-instagram" href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="font-display text-[#efe7d2] text-xl hover:text-[#d9b87d] transition-colors inline-flex items-center gap-2">
                    <Instagram size={16} strokeWidth={1.4} /> {SOCIAL_HANDLE}
                  </a>
                  <a data-testid="contact-tiktok" href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className="font-display text-[#efe7d2] text-xl hover:text-[#d9b87d] transition-colors inline-flex items-center gap-2">
                    <TikTokIcon size={16} /> {SOCIAL_HANDLE}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <form data-testid={HOME.contactForm} onSubmit={submit} className="lg:col-span-7 glass p-8 sm:p-12 space-y-6">
            <div>
              <label htmlFor="cname" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Name</label>
              <input id="cname" data-testid={HOME.contactName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-xl" placeholder="Your full name" />
            </div>
            <div>
              <label htmlFor="cemail" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Email</label>
              <input id="cemail" type="email" data-testid={HOME.contactEmailInput} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-xl" placeholder="you@company.com" />
            </div>
            <div>
              <label htmlFor="cmsg" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Message</label>
              <textarea id="cmsg" rows={5} data-testid={HOME.contactMessage} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] resize-none" placeholder="Introduce yourself and let us know what you're working on or interested in." />
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-[rgba(232,226,212,0.45)] italic">Replies typically within 1–2 business days.</span>
              <button data-testid={HOME.contactSubmit} type="submit" disabled={sending} className="btn-solid disabled:opacity-60">{sending ? "Sending..." : "Send Message"} <ArrowUpRight size={14} /></button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// =====================================================================================
// Footer
// =====================================================================================
const Footer = () => (
  <footer className="px-6 sm:px-10 py-12" style={{ borderTop: "1px solid rgba(232,226,212,0.08)" }}>
    <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center overflow-hidden" style={{ border: "1px solid rgba(232,226,212,0.25)" }}>
          <img src={ICH_LOGO} alt="ICH" className="w-full h-full object-contain p-1" />
        </div>
        <span className="font-label text-[rgba(232,226,212,0.55)]">© {new Date().getFullYear()} ICH Ventures · Innovative Capital Holdings LLC</span>
      </div>
      <div className="flex items-center gap-5 flex-wrap">
        <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="inline-flex items-center gap-2 text-[rgba(232,226,212,0.6)] hover:text-[#d9b87d] transition-colors">
          <Instagram size={16} strokeWidth={1.4} />
          <span className="font-label text-[rgba(232,226,212,0.6)] hover:text-[#d9b87d]" style={{ textTransform: "none", letterSpacing: "0.08em" }}>{SOCIAL_HANDLE}</span>
        </a>
        <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok" className="inline-flex items-center gap-2 text-[rgba(232,226,212,0.6)] hover:text-[#d9b87d] transition-colors">
          <TikTokIcon size={14} />
          <span className="font-label text-[rgba(232,226,212,0.6)] hover:text-[#d9b87d]" style={{ textTransform: "none", letterSpacing: "0.08em" }}>{SOCIAL_HANDLE}</span>
        </a>
        <a href={`mailto:${BUSINESS_EMAIL}`} className="font-label text-[rgba(232,226,212,0.6)] hover:text-[#d9b87d] transition-colors">{BUSINESS_EMAIL}</a>
      </div>
    </div>
  </footer>
);

// =====================================================================================
// Landing page
// =====================================================================================
const Home = () => (
  <div className="App">
    <div className="grain" />
    <Nav />
    <Hero />
    <About />
    <Services />
    <Consulting />
    <Contact />
    <Footer />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Routes>
        <Toaster position="bottom-right" theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
